import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, Image, Lock, Calendar, User } from 'lucide-react';

const API = '';

export default function SharedAlbum() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchSharedAlbum();
  }, [shareToken]);

  const fetchSharedAlbum = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/shared-album/${shareToken}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This share link has expired');
      } else if (err.response?.status === 404) {
        setError('Shared album not found or link is invalid');
      } else {
        setError('Failed to load shared album');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = `${API}${photo.file_url}`;
    link.download = `photo_${photo.photo_id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllPhotos = () => {
    if (!data?.photos) return;
    data.photos.forEach((photo, idx) => {
      setTimeout(() => downloadPhoto(photo), idx * 500);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading album...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Access Album</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const { album, photos, allow_download, shared_by, expires_at } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-purple-200 text-sm mb-2">
            <User className="w-4 h-4" />
            <span>Shared by {shared_by}</span>
          </div>
          <h1 className="text-3xl font-bold text-white">{album.name}</h1>
          {album.description && (
            <p className="text-purple-200 mt-2">{album.description}</p>
          )}
          <div className="flex items-center gap-4 mt-4 text-sm text-purple-200">
            <span className="flex items-center gap-1">
              <Image className="w-4 h-4" />
              {photos.length} photos
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Expires {new Date(expires_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Actions */}
        {allow_download && photos.length > 0 && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={downloadAllPhotos}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="download-all-btn"
            >
              <Download className="w-4 h-4" />
              Download All ({photos.length})
            </button>
          </div>
        )}

        {/* Photo Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.photo_id}
                className="group relative aspect-square bg-slate-700 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => setSelectedPhoto(photo)}
                data-testid={`photo-${photo.photo_id}`}
              >
                <img
                  src={`${API}${photo.file_url}`}
                  alt={photo.caption || 'Album photo'}
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
                {allow_download && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadPhoto(photo);
                    }}
                    className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-blue-600 text-white rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-blue-700 text-xs sm:text-sm"
                    title="Download photo"
                  >
                    <Download className="w-4 h-4" />
                    <span className="sm:hidden">Download</span>
                  </button>
                )}
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm truncate">{photo.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-xl p-12 text-center">
            <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No photos in this album yet</p>
          </div>
        )}

        {!allow_download && photos.length > 0 && (
          <p className="text-gray-500 text-center mt-6 text-sm">
            Downloads are not enabled for this shared album
          </p>
        )}

        {/* Lightbox */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="max-w-4xl max-h-full relative">
              <img
                src={`${API}${selectedPhoto.file_url}`}
                alt={selectedPhoto.caption || 'Photo'}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              {selectedPhoto.caption && (
                <p className="text-white text-center mt-4">{selectedPhoto.caption}</p>
              )}
              {allow_download && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPhoto(selectedPhoto);
                  }}
                  className="absolute top-4 right-4 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 left-4 p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Shared via Legacy Forge Family - Building Family Legacies Together
          </p>
        </div>
      </div>
    </div>
  );
}
